import {
  CELLO_REFERRAL_CACHE_KEY,
  CELLO_REFERRAL_QUERY_STRING,
  CELLO_REFERRER_NAME_QUERY_STRING,
} from '@constants/referral';

import { createChildLogger } from './logger';

const logger = createChildLogger({ module: 'modules/cello-referral' });

/**
 * Stripe promotion code carrying the referral discount: 10% off for 12 months.
 * The coupon behind it must be restricted to the products it may apply to, or
 * Stripe Checkout asks the user to type the code in by hand (ACT-1928).
 */
export const CELLO_REFERRAL_PROMOTION_CODE = 'CELLO10';

/** Discount the referral carries, in whole percent. Drives the copy and the coupon. */
export const CELLO_REFERRAL_DISCOUNT_PERCENT = 10;

/** Amplitude flag gating every referral surface: the paywall, the offer dialog and attribution. */
export const CELLO_REFERRAL_FLAG = 'cello-referral-paywall';

/** The discounted plan-selection step referred users are routed to. */
export const CELLO_REFERRAL_PAYWALL_ROUTE = '/onboarding/referral-paywall';

/** How long a referred user waits for the flag to resolve before their flow continues. */
const VARIANT_WAIT_MS = 2_000;
const VARIANT_POLL_MS = 100;

/**
 * Resolves once the referral flag has settled, or after `VARIANT_WAIT_MS`, whichever
 * comes first.
 *
 * The experiment provider refetches variants when the Firebase identity changes, so
 * immediately after an authentication the variant on hand can still be the one resolved
 * for the signed-out device — reading it too early drops the offer for a user who is
 * bucketed in. Bounded on purpose: a slow or failed variant fetch must not hold a signup
 * open, and falling through means the user simply doesn't get the offer this time. Only
 * referred users ever wait.
 */
export async function waitForCelloReferralVariant(loadingRef: { current: boolean }): Promise<void> {
  const attempts = VARIANT_WAIT_MS / VARIANT_POLL_MS;

  for (let attempt = 0; attempt < attempts && loadingRef.current; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, VARIANT_POLL_MS));
  }
}

/**
 * `window.localStorage`, or `undefined` where it can't be reached — during SSR,
 * and in browsers that refuse it (private modes, sandboxed iframes, storage
 * blocked by the user), where merely touching the property throws. A missing
 * referral discount is worth degrading for; a crashed onboarding flow isn't.
 */
function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Caches the Cello referral code (`ucc`) from the landing URL so it survives the
 * signup redirects and is still available at the paywall.
 *
 * Cello appends the parameter to whatever destination URL is configured in its
 * dashboard, so this runs on every page rather than one known route — and in both
 * routers, since either can be the landing page. Uses `localStorage` because signup
 * can bounce through email verification.
 */
function storeCelloReferralCode(code: string | null): void {
  if (!code) {
    return;
  }

  try {
    getStorage()?.setItem(CELLO_REFERRAL_CACHE_KEY, code);
    logger.debug('Saved Cello referral code to local storage');
  } catch (error) {
    logger.warn('Could not save Cello referral code to local storage', { error });
  }
}

/** Captures the referral code from a Pages Router `router.query`. */
export function cacheCelloReferralCode(query: Record<string, string | string[] | undefined>): void {
  storeCelloReferralCode(getCelloReferralCodeFromQuery(query));
}

/**
 * Captures the referral code from App Router search params, which carry no
 * Pages Router query shape.
 */
export function cacheCelloReferralCodeFromSearchParams(searchParams: URLSearchParams | null): void {
  storeCelloReferralCode(searchParams?.get(CELLO_REFERRAL_QUERY_STRING) ?? null);
}

export function getCachedCelloReferralCode(): string | null {
  try {
    return getStorage()?.getItem(CELLO_REFERRAL_CACHE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Consumes the referral: the cached code is what every surface reads to decide
 * whether to offer the discount, so clearing it is how an offer is retired.
 * Called when the user acts on an offer — takes it or declines it — never on
 * render, which would retire the offer out from under a live dialog the moment
 * anything remounted it.
 *
 * Retirement is per cached code, not per user: landing on a URL that still carries
 * `ucc` re-caches it and the offer stands again. Pressing back onto the referral
 * link after declining therefore re-offers the discount. That is deliberate — the
 * code is still valid, and arriving on the link again is the same act that offered
 * it in the first place. Suppressing it would need a second piece of state whose
 * only job is to remember a refusal.
 */
export function clearCachedCelloReferralCode(): void {
  try {
    getStorage()?.removeItem(CELLO_REFERRAL_CACHE_KEY);
  } catch {
    // Nothing to recover: the code either isn't stored or can't be reached.
  }
}

type QueryValue = string | string[] | undefined;

function firstQueryValue(value: QueryValue): string | null {
  const single = Array.isArray(value) ? value[0] : value;
  return single || null;
}

export function getCelloReferralCodeFromQuery(query: Record<string, QueryValue>): string | null {
  return firstQueryValue(query[CELLO_REFERRAL_QUERY_STRING]);
}

export function getCelloReferrerNameFromQuery(query: Record<string, QueryValue>): string | null {
  const encoded = firstQueryValue(query[CELLO_REFERRER_NAME_QUERY_STRING]);

  if (!encoded) {
    return null;
  }

  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const name = new TextDecoder().decode(bytes).trim();

    return name || null;
  } catch {
    return null;
  }
}

export function getFirstName(name: string | null): string | null {
  return name?.trim().split(/\s+/)[0] || null;
}
